/**
 * @file   src\mobile\components\SendIntroduction\MakeIntroductionOffcanvas.js
 * @brief  This file is responsible for send introduction functionality
 * @date   May, 2024
 * @author ZCO Engineer
 * @copyright (c) 2024, ZCO
 */
import React, { useState, useEffect, useRef } from "react";
import { Offcanvas, OverlayTrigger, Tooltip } from "react-bootstrap";
import TextAreaMob from "../TextAreaMob";
import TextBoxMob from "../TextBoxMob";
import { useDispatch, useSelector } from "react-redux";
import { fetchInventoryDetailsById } from "../../../actions/inventory";
import {
  getLocalStorage,
  getUserId,
  getUserType,
  removeLocalStorage,
  replaceComma,
  saveTutorialStatusToLocal,
  setLocalStorage,
  thousandSeperatedValue,
  trimString,
  upperCaseEachSentenceFirst,
} from "../../../helpers/common";
import MemberPropertiesData from "./MemberPropertiesData";
import AgentPropertyDetails from "./AgentPropertyDetails";
import InfoIcon from "../infoIcn";
import {
  AGENT_INTRODUCTION,
  ERROR_TEXT,
  MEMBER_INTRODUCTION,
  USER_TYPE_AGENT,
  USER_TYPE_MEMBER,
} from "../../../constants/common";
import SelectClient from "./SelectClient";
import { getIntroClientList } from "../../../services/agentClientsService";
import SelectProperty from "./SelectProperty";
import { getPropertyList } from "../../../services/inventoryService";
import { handleTextBoxChange } from "../../../actions/common";
import {
  removeNegativeInput,
  textValidations,
} from "../../../helpers/validations";
import AddNewClient from "../../commonComponents/ClientPopup";
import { fetchUserDetails } from "../../../actions/userprofile";
import JoyRide, { STATUS } from "react-joyride";
import IntroductionTutorialIconStep1 from "../../../images/tut-property-icon.svg";
import IntroductionTutorialIconStep2 from "../../../images/tut-askingprice-icon.svg";
import IntroductionTutorialIconStep3 from "../../../images/tut-custommsg-icon.svg";
import IntroductionTutorialIconStep4 from "../../../images/tut-send-icon.svg";
import { saveTutorialStatus } from "../../../services/commonServices";
import DocumentationTutorialMob from "../../../components/Tutorial/DocumentationTutorialMob";
import { REVIEW_ORDER } from "../../../constants/onBoarding";

const MakeIntroductionOffcanvas = (props) => {
  const {
    show,
    onHide,
    setUIKey,
    displayTutorial,
    setDisplayTutorial,
    isPremiumUser,
  } = props;
  const dispatch = useDispatch();
  const tutorialPage = 5; //send intro tutorial
  const wantAdId = props.wantAdId;
  const introductionSendingStatus = props.introductionSendingStatus;
  const [redirectedFromReorder, setRedirectedFromReorder] = useState(
    introductionSendingStatus || false
  );
  const [clientList, setClientList] = useState(null);
  const [defaultClient, setDefaultClient] = useState(null);

  const [propertyList, setPropertyList] = useState([]);
  const userId = getUserId();
  const userDetailsFromStore = useSelector(
    (state) => state.userProfile.userDetails
  );
  const [userDetails, setUserDetails] = useState(null);
  const userType = getUserType();
  let session = userType == 2 ? AGENT_INTRODUCTION : MEMBER_INTRODUCTION;
  let sessionData = getLocalStorage(session);
  const [defaultProperty, setDefaultProperty] = useState(
    redirectedFromReorder ? sessionData?.selectedProperty : null
  );
  const [selectedClient, setSelectedClient] = useState("");
  const [showSelectProperty, setShowSelectProperty] = useState(false);
  const inventoryDetails = useSelector(
    (state) => state.inventory.inventoryDetails
  );
  const [selectedProperty, setSelectedProperty] = useState("");
  const [memberIntroduction, setMemberIntroduction] = useState("");
  const [messageCharCount, setMessageCharCount] = useState(0);
  const [msgError, setMsgError] = useState("");
  const [showSelectClients, setShowSelectClients] = useState(false);
  const ShowSelectClientsClose = () => setShowSelectClients(false);
  const ShowSelectClientsShow = () => setShowSelectClients(true);
  const [searchText, setSearchText] = useState(null);
  const [showAddNewClient, setAddNewClientShow] = useState(false);

  const [messageCursorPos, setMessageCursorPos] = useState(0);
  const [askingPriceCursorPos, setAskingPriceCursorPos] = useState(0);
  const messageRef = useRef();
  const askingpriceRef = useRef();

  const handleAddNewClientClose = (agentId) => {
    setAddNewClientShow(false);
    ShowSelectClientsShow();
    reloadClientList();
  };

  const handleAddNewClientShow = () => {
    setAddNewClientShow(true);
    ShowSelectClientsClose();
  };

  //form States
  const [formData, setFormData] = useState(sessionData);

  useEffect(() => {
    getClientList();
  }, [userId]);

  useEffect(() => {
    removeLocalStorage(session);
  }, [!redirectedFromReorder]);

  useEffect(() => {
    getpropertyList();
  }, [selectedClient?.ClientId]);

  useEffect(() => {
    if (userId) {
      dispatch(
        fetchUserDetails({
          userId: userId,
        })
      );
    }
  }, []);
  useEffect(() => {
    if (userDetailsFromStore !== null && userDetailsFromStore?.length > 0) {
      setUserDetails(userDetailsFromStore[0]);
    } else {
      setUserDetails(null);
    }
  }, [userDetailsFromStore]);

  useEffect(() => {
    setSelectedProperty(defaultProperty);
    let initailData = {
      userId: userId,
      wantAdId: wantAdId,
      defaultAskingPrice: defaultProperty?.AskingPrice,
      selectedProperty: defaultProperty,
      inventoryId: defaultProperty?.Inv_Id,
      wantAdDetails: props.wantAdDetails,
      askingPrice:
        defaultProperty?.AskingPrice == formData?.defaultAskingPrice
          ? formData?.askingPrice
          : "",
      message:
        defaultProperty?.AskingPrice == formData?.defaultAskingPrice
          ? formData?.message
          : "",
    };
    setFormData((formData) => ({ ...formData, ...initailData }));
  }, [propertyList, defaultProperty]);

  useEffect(() => {
    setSelectedClient(defaultClient);
    let initailData = {
      selectedClient: defaultClient,
      clientId: defaultClient?.ClientId,
    };
    setFormData((formData) => ({ ...formData, ...initailData }));
  }, [clientList]);

  useEffect(() => {
    setLocalStorage(session, formData);
    setMemberIntroduction(getLocalStorage(session));
  }, [formData]);

  useEffect(() => {
    if (formData?.inventoryId) {
      dispatch(fetchInventoryDetailsById(formData?.inventoryId));
    }
  }, [formData?.inventoryId]);

  useEffect(() => {
    let initailData = {
      wantAdDetails: props.wantAdDetails,
    };
    setFormData((formData) => ({ ...formData, ...initailData }));
  }, [props.wantAdDetails]);

  useEffect(() => {
    if (messageRef.current) {
      messageRef.current.setSelectionRange(messageCursorPos, messageCursorPos);
    }
  }, [messageCursorPos]);

  useEffect(() => {
    if (askingpriceRef.current) {
      askingpriceRef.current.setSelectionRange(
        askingPriceCursorPos,
        askingPriceCursorPos
      );
    }
  }, [askingPriceCursorPos]);

  /**
   * To handle textbox value change
   * @param {any} event
   */
  const onChangeText = (event) => {
    let value = handleTextBoxChange(event);
    if (event.target.name == "message") {
      value = upperCaseEachSentenceFirst(value);
      setMessageCharCount(value.length);
      if (trimString(value) !== "" && textValidations(value)) {
        setMsgError("");
        setMessageCursorPos(event.target.selectionStart);
      } else {
        setMsgError(trimString(value) !== "" ? ERROR_TEXT : "");
      }
    }
    if (event.target.name == "askingPrice") {
      value = removeNegativeInput(value);
      value = replaceComma(value);
      if (value !== "") {
        if (
          thousandSeperatedValue(memberIntroduction?.askingPrice)?.length + 1 ==
          thousandSeperatedValue(event.target.value)?.length
        ) {
          setAskingPriceCursorPos(event.target.selectionStart);
        } else if (
          thousandSeperatedValue(memberIntroduction?.askingPrice)?.length + 2 ==
          thousandSeperatedValue(event.target.value)?.length
        ) {
          setAskingPriceCursorPos(event.target.selectionStart + 1);
        } else {
          setAskingPriceCursorPos(event.target.selectionStart);
        }
      }
    }
    setFormData((formData) => ({ ...formData, [event.target.name]: value }));
  };
  /**
   * To handle selected property change
   * @param {*} property
   */
  const handlePropertyChange = (property) => {
    setSelectedProperty(property);
    setFormData((formData) => ({
      ...formData,
      inventoryId: property?.Inv_Id,
      selectedProperty: property,
      defaultAskingPrice: property?.AskingPrice,
      askingPrice: "",
      message: "",
      isAdhereFFHAct: false,
      isRepresentOwner: false,
    }));
  };

  const handleClientChange = (client) => {
    setSelectedClient(client);
    setFormData((formData) => ({
      clientId: client?.ClientId,
      selectedClient: client,
    }));
  };

  useEffect(() => {
    let photosAfterRemovingInvalid =
      inventoryDetails?.photos &&
      inventoryDetails?.photos.filter((item) => item.IsValid != 0);
    let photos =
      photosAfterRemovingInvalid &&
      photosAfterRemovingInvalid.map((photo, index) => {
        return { ...photo, order: index + 1 };
      });

    let inventoryData = {
      originalPhotos: photos, // data from db to customize
      photosForPreview: photos, // data from db to customize
      originalpropertyFeatures: inventoryDetails?.nice2Have, // data from db to customize
      propertyFeaturesForPreview: inventoryDetails?.nice2Have, // data from db to customize
      noBedRm: inventoryDetails?.noBedRm,
      noBathRm: inventoryDetails?.noBathRm,
      sqFt: inventoryDetails?.sqFt,
      lotSize: inventoryDetails?.lotSize,
      locAddress: inventoryDetails?.locAddress,
      salesPercnt: inventoryDetails?.salesPercnt,
      insReport: inventoryDetails?.insReport,
      apprOnFile: inventoryDetails?.apprOnFile,
      floodCert: inventoryDetails?.floodCert,
      s3ImageFolder: inventoryDetails?.s3ImageFolder,
      propertyTypeId: inventoryDetails?.prprtyType,
      acreage: inventoryDetails?.acreage,
      lotType: inventoryDetails?.lotType,
      timeLineTxt: inventoryDetails?.timeLineTxt,
      floorPlan: inventoryDetails?.floorPlan,
      sellerFin: inventoryDetails?.sellerFin,
      mapImg: inventoryDetails?.mapImg,
      city: inventoryDetails?.city,
      state: inventoryDetails?.state,
      zip: inventoryDetails?.zip,
      lat: inventoryDetails?.lat,
      lng: inventoryDetails?.lng,
    };
    setFormData((formData) => ({ ...formData, ...inventoryData }));
  }, [inventoryDetails]);

  const reloadClientList = (searchText) => {
    if (searchText) {
      setSearchText(searchText);
    }
    getClientList(searchText);
  };

  const setShowSelectClientsAction = () => {
    props.onHide();
    setShowSelectClients(true);
  };

  const setShowSelectPropertyAction = () => {
    props.onHide();
    setShowSelectProperty(true);
  };

  const handleIntroductionPopup = () => {
    setShowSelectClients(false);
    setShowSelectProperty(false);
    props.showIntroductionPopup();
  };
  /**
   * To get clients list
   * @param {*} searchText
   */
  const getClientList = async (searchText = null) => {
    if (userId) {
      await getIntroClientList(userId, searchText)
        .then((response) => {
          const responseResult = _.get(response, "result", null);
          if (response.errorCode == 0 && responseResult) {
            setClientList(responseResult?.ClientList);

            setDefaultClient(
              introductionSendingStatus
                ? sessionData.selectedClient
                  ? sessionData.selectedClient
                  : responseResult?.DefaultClient.length > 0
                  ? responseResult?.DefaultClient
                  : responseResult?.ClientList[0]
                : responseResult?.DefaultClient.length > 0
                ? responseResult?.DefaultClient
                : responseResult?.ClientList[0]
            );
          } else {
            // toast.error("Something went wrong");
          }
        })
        .catch((err) => {
          // toast.error("Error", err);
        });
    }
  };
  /**
   * To get property list
   */
  const getpropertyList = async () => {
    if (userId) {
      let query;
      if (userType == 2 && isPremiumUser) {
        query = {
          wantAdId: wantAdId,
          clientId: selectedClient?.ClientId,
        };
      } else {
        query = {
          wantAdId: wantAdId,
        };
      }
      await getPropertyList(query)
        .then((response) => {
          const responseResult = _.get(response, "result", null);
          if (response.errorCode === 0 && responseResult) {
            setPropertyList(responseResult?.propertyList);
            let defaultArray =
              responseResult?.defaultProperty.length > 0
                ? responseResult?.defaultProperty
                : responseResult?.propertyList[0];

            setDefaultProperty(
              redirectedFromReorder
                ? sessionData.selectedProperty
                  ? sessionData.selectedProperty
                  : defaultArray
                : defaultArray
            );

            setRedirectedFromReorder(false);
          } else {
            // toast.error("Something went wrong");
          }
        })
        .catch((error) => console.log(error));
    }
  };
  /**
   * To render tootltip
   * @param {*} props
   * @returns
   */
  const renderTooltip = (props) => (
    <Tooltip id="button-tooltip" {...props}>
      This number is meant to start a conversation and can be changed as you
      negotiate.
    </Tooltip>
  );

  const [tutorialSteps, setTutorialSteps] = useState([
    {
      target: "#send-intro-tutorial",
      disableBeacon: true,
      placement: "left",
      title: "Choose a property to share",
      icon: IntroductionTutorialIconStep1,
      content:
        "If you have multiple properties, you can swap out which one you want to introduce.",
      hideCloseButton: true,
      showProgress: true,
      showSkipButton: true,
    },
    {
      target: "#askingprice",
      disableBeacon: true,
      placement: "bottom",
      title: "Adjust asking price",
      icon: IntroductionTutorialIconStep2,
      content:
        "This is just conversation starter. So try not to overthink it. You can always change it later.",
      hideCloseButton: true,
      showProgress: true,
      showSkipButton: true,
    },
    {
      target: ".property-message-tutorial",
      disableBeacon: true,
      placement: "top",
      title: "Write a custom message",
      icon: IntroductionTutorialIconStep3,
      content:
        "This is your first chance to tell them why you think your property is a perfect match.",
      hideCloseButton: true,
      showProgress: true,
      showSkipButton: true,
    },
    {
      target: ".sendMessageBtn-tutorial",
      disableBeacon: true,
      placement: "left",
      title: "Review and send",
      icon: IntroductionTutorialIconStep4,
      content:
        "You'll get another chance to review your introduction before you finalize and send.",
      hideCloseButton: true,
      showProgress: true,
    },
  ]);
  //Tutorial tooltip
  const TutorialToolTip = ({
    continuous,
    index,
    step,
    backProps,
    primaryProps,
    tooltipProps,
    size,
    isLastStep,
    skipProps,
  }) => (
    <>
      <div {...tooltipProps}>
        <DocumentationTutorialMob
          continuous={continuous}
          index={index}
          step={step}
          backProps={backProps}
          skipProps={skipProps}
          primaryProps={primaryProps}
          isLastStep={isLastStep}
          size={size}
          finalStepBtn={"Got It!"}
        />
      </div>
    </>
  );
  //Tutorial status call back
  const handleJoyrideCallback = async (data) => {
    const { action, index, status, type, lifecycle } = data;
    if (
      [STATUS.FINISHED, STATUS.SKIPPED].includes(status) &&
      lifecycle == "complete"
    ) {
      setDisplayTutorial(false);
      saveTutorialStatusToLocal(tutorialPage);
      await saveTutorialStatus(tutorialPage, 1); //Save status
    }
  };

  return (
    <>
      {showSelectClients && (
        <SelectClient
          clientList={clientList}
          selectedClient={selectedClient}
          setSelectedClient={handleClientChange}
          setShowSelectClients={ShowSelectClientsShow}
          showSelectClients={showSelectClients}
          onClose={ShowSelectClientsClose}
          handleIntroductionPopup={handleIntroductionPopup}
          reloadClientList={reloadClientList}
          AddNewClient={handleAddNewClientShow}
        />
      )}

      {showSelectProperty && (
        <SelectProperty
          setShowSelectProperty={setShowSelectProperty}
          propertyList={propertyList}
          selectedProperty={selectedProperty}
          setSelectedProperty={handlePropertyChange}
          userType={userType}
          selectedClient={selectedClient}
          userDetails={userDetails}
          showSelectProperty={showSelectProperty}
          handleIntroductionPopup={handleIntroductionPopup}
          setUIKey={setUIKey}
        />
      )}

      <Offcanvas
        className="mobile-offcanvas offcanvas-fixed offcanvas-introduction offcanvas-introduction-height-fix btm-pad-0"
        show={show}
        onHide={onHide}
        onDragDown={onHide}
        placement="bottom"
        {...props}
      >
        <Offcanvas.Header>
          <a
            className="back-link-btn"
            href={void 0}
            onClick={() => {
              onHide();
            }}
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M10.1003 5.39966C10.3932 5.69256 10.3932 6.16743 10.1003 6.46032L5.31066 11.25H20.5C20.9142 11.25 21.25 11.5858 21.25 12C21.25 12.4142 20.9142 12.75 20.5 12.75H5.31066L10.1003 17.5397C10.3932 17.8326 10.3932 18.3074 10.1003 18.6003C9.80744 18.8932 9.33256 18.8932 9.03967 18.6003L2.96967 12.5303C2.82902 12.3897 2.75 12.1989 2.75 12C2.75 11.8011 2.82902 11.6103 2.96967 11.4697L9.03967 5.39966C9.33256 5.10677 9.80744 5.10677 10.1003 5.39966Z"
                fill="#1B1C1E"
              />
            </svg>
          </a>
        </Offcanvas.Header>
        <Offcanvas.Body className="offcanvas-body-btm-pad">
          <div className="make-introduction-wrap d-flex flex-column">
            <div className="title-block">
              <div className="intro-icon">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <path
                    d="M14.2333 3.00384C13.731 3.02709 13.2305 3.15613 12.7636 3.38958C12.7501 3.39634 12.7367 3.4035 12.7236 3.41107L11.3056 4.2304C11.2906 4.22963 11.2757 4.2293 11.2607 4.22943H1.76068C1.6613 4.22802 1.56263 4.24638 1.4704 4.28344C1.37817 4.3205 1.29423 4.37552 1.22346 4.44531C1.15268 4.51509 1.09648 4.59824 1.05812 4.68994C1.01975 4.78163 1 4.88003 1 4.97943C1 5.07882 1.01975 5.17722 1.05812 5.26892C1.09648 5.36061 1.15268 5.44376 1.22346 5.51355C1.29423 5.58333 1.37817 5.63835 1.4704 5.67541C1.56263 5.71247 1.6613 5.73083 1.76068 5.72943H8.14252L7.81635 5.86517C7.80015 5.87177 7.78419 5.87894 7.76849 5.88665C6.93732 6.30224 6.55386 7.18755 6.63666 7.98431C6.71945 8.78106 7.28985 9.57538 8.20404 9.80364C8.5604 9.89243 8.95624 9.94427 9.38764 9.94427C11.5744 9.94427 13.7173 9.26287 14.3515 9.05267C14.7685 9.51828 16.8253 11.8167 17.5126 12.5204C18.0104 13.0298 18.0608 13.5271 17.9326 13.8808C17.8043 14.2345 17.5132 14.5078 16.8788 14.5097C16.7066 14.5102 16.5397 14.57 16.4064 14.679C16.273 14.788 16.1812 14.9396 16.1464 15.1083C15.9594 16.0127 15.6294 16.1411 15.2929 16.2294C14.9564 16.3178 14.6318 16.2411 14.6318 16.2411C14.5224 16.2113 14.4077 16.2066 14.2962 16.2275C14.1848 16.2483 14.0795 16.2942 13.9884 16.3616C13.8972 16.4289 13.8225 16.5161 13.7699 16.6166C13.7172 16.717 13.688 16.828 13.6845 16.9413C13.6845 16.9413 13.6596 17.3286 13.4687 17.6601C13.2778 17.9916 13.0789 18.271 12.1884 18.1738C11.9994 18.153 11.8096 18.2047 11.6573 18.3186C11.505 18.4324 11.4016 18.5999 11.3681 18.787C11.266 19.3578 10.9556 19.6056 10.4902 19.6913C10.0247 19.777 9.39263 19.6247 8.83978 19.0781C8.13192 18.3785 5.55091 15.7463 4.38275 14.5536C3.86554 14.0254 3.15721 13.7294 2.41986 13.7294H1.76068C1.6613 13.728 1.56263 13.7464 1.4704 13.7834C1.37817 13.8205 1.29423 13.8755 1.22346 13.9453C1.15268 14.0151 1.09648 14.0982 1.05812 14.1899C1.01975 14.2816 1 14.38 1 14.4794C1 14.5788 1.01975 14.6772 1.05812 14.7689C1.09648 14.8606 1.15268 14.9438 1.22346 15.0135C1.29423 15.0833 1.37817 15.1383 1.4704 15.1754C1.56263 15.2125 1.6613 15.2308 1.76068 15.2294H2.41986C2.75851 15.2294 3.07568 15.3627 3.31146 15.6034C4.48031 16.7968 7.03696 19.4051 7.7851 20.1445C8.64775 20.9974 9.76012 21.3513 10.7617 21.1669C11.5738 21.0174 12.285 20.451 12.6406 19.6435C13.4959 19.5721 14.153 19.1793 14.5497 18.7109C14.5598 18.7114 14.5699 18.7117 14.58 18.7118C14.58 18.7118 16.837 18.7512 18.0927 18.7118C18.9663 18.6848 19.8469 18.3883 20.624 17.707C20.9745 17.3998 21.3962 17.2284 21.8251 17.2284H22.2607C22.3601 17.2299 22.4587 17.2115 22.551 17.1744C22.6432 17.1374 22.7271 17.0824 22.7979 17.0126C22.8687 16.9428 22.9249 16.8596 22.9632 16.7679C23.0016 16.6762 23.0214 16.5778 23.0214 16.4784C23.0214 16.3791 23.0016 16.2807 22.9632 16.189C22.9249 16.0973 22.8687 16.0141 22.7979 15.9443C22.7271 15.8745 22.6432 15.8195 22.551 15.7825C22.4587 15.7454 22.3601 15.727 22.2607 15.7284H21.8251C20.9981 15.7284 20.2302 16.0582 19.6357 16.579C19.0897 17.0577 18.6563 17.1939 18.0468 17.2128H18.0458C17.6363 17.2257 17.066 17.223 16.5292 17.2226C16.9118 16.9072 17.255 16.4487 17.4667 15.8359C18.3332 15.6378 19.0676 15.1512 19.3427 14.3925C19.6822 13.4561 19.4181 12.3243 18.5849 11.4716C17.9535 10.8252 15.1503 7.70208 15.1503 7.70208C15.052 7.59235 14.9235 7.51401 14.7809 7.47684C14.6383 7.43968 14.488 7.44533 14.3486 7.4931C14.3486 7.4931 11.4922 8.44427 9.38764 8.44427C9.07603 8.44427 8.80496 8.40777 8.56732 8.34857C8.26802 8.27383 8.15493 8.08003 8.12885 7.82904C8.10291 7.57947 8.18504 7.35914 8.43646 7.23138L10.7841 6.25677C10.8143 6.24406 10.8437 6.22938 10.872 6.21282L13.4384 4.72943C14.0055 4.44715 14.608 4.42589 15.1542 4.64446L18.5341 5.99603C18.9182 6.15033 19.3284 6.22833 19.7411 6.22845L22.2607 6.22943C22.3601 6.23083 22.4587 6.21247 22.551 6.17541C22.6432 6.13835 22.7271 6.08333 22.7979 6.01355C22.8687 5.94376 22.9249 5.86061 22.9632 5.76892C23.0016 5.67722 23.0214 5.57882 23.0214 5.47943C23.0214 5.38003 23.0016 5.28163 22.9632 5.18994C22.9249 5.09824 22.8687 5.01509 22.7979 4.94531C22.7271 4.87552 22.6432 4.8205 22.551 4.78344C22.4587 4.74638 22.3601 4.72802 22.2607 4.72943L19.7421 4.72845C19.5193 4.72845 19.2993 4.687 19.0937 4.60443L19.0917 4.60345L15.7119 3.25189C15.2396 3.06292 14.7357 2.98059 14.2333 3.00384Z"
                    fill="#1B1C1E"
                  />
                </svg>
              </div>
              <h3>Send Them Your Property</h3>
              <p>
                Do you own or represent a property that sounds like a match?
                Introduce it!
              </p>
            </div>
            <div
              className="property-outer-wrap d-flex flex-column"
              id="send-intro-tutorial"
            >
              {userType === USER_TYPE_AGENT && isPremiumUser ? (
                <AgentPropertyDetails
                  propertyList={propertyList}
                  setShowSelectProperty={setShowSelectPropertyAction}
                  setShowSelectClients={setShowSelectClientsAction}
                  clientList={clientList}
                  userId={userId}
                  selectedClient={selectedClient}
                  setSelectedClient={setSelectedClient}
                  selectedProperty={selectedProperty}
                  userDetails={userDetails}
                />
              ) : (
                <MemberPropertiesData
                  propertyList={propertyList}
                  setShowSelectProperty={setShowSelectPropertyAction}
                  selectedProperty={selectedProperty}
                  userDetails={userDetails}
                />
              )}
            </div>

            <div className="addyour-property-details-wrap position-relative property-price">
              <div className="property-dollar-icon">$</div>

              <label className="form-label mr-6">
                Asking Price <span>*</span>
              </label>
              <OverlayTrigger
                placement="bottom"
                delay={{ show: 250, hide: 400 }}
                overlay={renderTooltip}
              >
                <a href={void 0} className="tooltip-icon">
                  <InfoIcon />
                </a>
              </OverlayTrigger>

              <TextBoxMob
                id="askingprice"
                name="askingPrice"
                type="text"
                pattern="[0-9]*"
                inputmode="numeric"
                value={
                  formData?.askingPrice
                    ? thousandSeperatedValue(formData?.askingPrice)
                    : ""
                }
                placeholder={
                  memberIntroduction?.defaultAskingPrice
                    ? thousandSeperatedValue(
                        memberIntroduction?.defaultAskingPrice
                      )
                    : 0
                }
                onChange={(e) => {
                  onChangeText(e);
                }}
                className="property-txt-fld property-txt-leftpad"
                txtRef={askingpriceRef}
                disabled={
                  selectedProperty?.Inv_Id &&
                  selectedProperty?.photo &&
                  ((selectedProperty?.IsIntroSent == 1 &&
                    selectedProperty?.IsCancelled == 1) ||
                    selectedProperty?.IsIntroSent == 0 ||
                    !selectedProperty?.IsIntroSent) &&
                  (selectedProperty?.IsDecline == 0 ||
                    !selectedProperty?.IsDecline) &&
                  (selectedProperty?.NotInterested == 0 ||
                    !selectedProperty?.NotInterested) &&
                  (selectedProperty?.Not_A_Fit == 0 ||
                    !selectedProperty?.Not_A_Fit)
                    ? false
                    : true
                }
              />
            </div>

            <div className="field-group mb-3">
              <div className="mob-fld-wrap">
                <label className="form-label">Message (Optional)</label>
              </div>

              <TextAreaMob
                name="message"
                maxLength={450}
                className="property-message-tutorial"
                errorMessage={msgError}
                value={formData?.message ? formData?.message : ""}
                placeholder="Hello! I think my property is exactly what you’re looking for. "
                onChange={(e) => {
                  onChangeText(e);
                }}
                txtRef={messageRef}
                disabled={
                  selectedProperty?.Inv_Id &&
                  selectedProperty?.photo &&
                  ((selectedProperty?.IsIntroSent == 1 &&
                    selectedProperty?.IsCancelled == 1) ||
                    selectedProperty?.IsIntroSent == 0 ||
                    !selectedProperty?.IsIntroSent) &&
                  (selectedProperty?.IsDecline == 0 ||
                    !selectedProperty?.IsDecline) &&
                  (selectedProperty?.NotInterested == 0 ||
                    !selectedProperty?.NotInterested) &&
                  (selectedProperty?.Not_A_Fit == 0 ||
                    !selectedProperty?.Not_A_Fit)
                    ? false
                    : true
                }
              />
            </div>
            <div className="inner-btm-fixed-nav">
              <div className="btn-wrap text-center">
                <button
                  type="button"
                  className="btn btn-primary w-100 sendMessageBtn-tutorial"
                  disabled={
                    selectedProperty?.Inv_Id &&
                    !msgError &&
                    selectedProperty?.photo &&
                    ((selectedProperty?.IsIntroSent == 1 &&
                      selectedProperty?.IsCancelled == 1) ||
                      selectedProperty?.IsIntroSent == 0 ||
                      !selectedProperty?.IsIntroSent) &&
                    (selectedProperty?.IsDecline == 0 ||
                      !selectedProperty?.IsDecline) &&
                    (selectedProperty?.NotInterested == 0 ||
                      !selectedProperty?.NotInterested) &&
                    (selectedProperty?.Not_A_Fit == 0 ||
                      !selectedProperty?.Not_A_Fit)
                      ? false
                      : true
                  }
                  onClick={(e) => {
                    e.preventDefault();
                    props.setUIKey(REVIEW_ORDER);
                  }}
                >
                  Review Introduction
                </button>
              </div>
            </div>
          </div>
        </Offcanvas.Body>
        {displayTutorial ? (
          <JoyRide
            steps={tutorialSteps}
            tooltipComponent={TutorialToolTip}
            continuous
            callback={handleJoyrideCallback}
            disableScrolling={false}
            scrollToSteps={true}
            styles={{
              options: {
                zIndex: 10000,
              },
            }}
          />
        ) : (
          ""
        )}
      </Offcanvas>

      <AddNewClient
        show={showAddNewClient}
        onHide={handleAddNewClientClose}
        clientDetails={null}
        isEdit={false}
        isFromIntroduction={true}
      />
    </>
  );
};
export default MakeIntroductionOffcanvas;
